/**
 * API client for the reward-system Edge Function.
 * Handles social share rewards, reviewer link rewards, referrals, and reward notifications.
 */

import { requireAuth, invokeEdgeFn } from './supabaseClient.ts';

// ─── Types ──────────────────────────────────────────────────────────────

export interface RewardResult {
  rewarded: boolean;
  reason?: string;
  credits_awarded?: number;
  balance?: { available: number; used: number; is_beta: boolean } | null;
}

export interface PendingReward {
  id: string;
  event_type: 'social_share' | 'reviewer_link' | 'referral_given' | 'referral_received';
  credits_awarded: number;
  created_at: string;
}

export interface ReferralInfo {
  referral_code: string | null;
  has_been_referred: boolean;
  monthly_referrals: number;
  monthly_cap: number;
  total_referrals: number;
  total_credits_earned: number;
  pending_rewards: PendingReward[];
}

// ─── Balance sync (same pattern as aiApi.ts) ────────────────────────────

function syncBalance(data: any): void {
  if (data?.balance) {
    try {
      import('@/store/accountStore.ts').then(({ useAccountStore }) => {
        useAccountStore.getState().setAccountBalance({
          available: data.balance.available,
          used: data.balance.used,
          isBeta: data.balance.is_beta,
        });
      });
    } catch { /* ignore */ }
  }
}

async function invokeRewardFn(body: Record<string, unknown>): Promise<any> {
  return invokeEdgeFn('reward-system', body, { onResponse: syncBalance });
}

// ─── API calls ──────────────────────────────────────────────────────────

/** Record a social share and claim the 25-credit reward (7-day cooldown). */
export async function recordSocialShare(metadata?: Record<string, unknown>): Promise<RewardResult> {
  await requireAuth();
  return invokeRewardFn({ action: 'record-share', event_type: 'social_share', metadata });
}

/** Record first reviewer link creation and claim the one-time 25-credit reward. */
export async function recordReviewerLink(sessionId?: string): Promise<RewardResult> {
  await requireAuth();
  return invokeRewardFn({
    action: 'record-share',
    event_type: 'reviewer_link',
    metadata: sessionId ? { session_id: sessionId } : {},
  });
}

/** Claim a referral code — credits both users 50 credits. */
export async function claimReferral(referralCode: string): Promise<RewardResult> {
  await requireAuth();
  return invokeRewardFn({ action: 'claim-referral', referral_code: referralCode });
}

/** Get referral code, stats, and pending reward notifications. */
export async function getReferralInfo(): Promise<ReferralInfo> {
  await requireAuth();
  return invokeRewardFn({ action: 'get-referral-info' });
}

/** Mark all pending rewards as acknowledged. */
export async function acknowledgeRewards(): Promise<void> {
  await requireAuth();
  await invokeRewardFn({ action: 'acknowledge-rewards' });
}
